import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Module-themed primary button
        default: "bg-[hsl(var(--module-primary))] text-white hover:bg-[hsl(var(--module-primary-dark))] shadow-sm hover:shadow-md",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // Module-themed outline with module color hover
        outline: "border border-input bg-background hover:bg-[hsl(var(--module-primary)/0.1)] hover:text-[hsl(var(--module-primary))] hover:border-[hsl(var(--module-primary)/0.5)]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        // Module-themed ghost with module color hover
        ghost: "hover:bg-[hsl(var(--module-primary)/0.1)] hover:text-[hsl(var(--module-primary))]",
        link: "text-[hsl(var(--module-primary))] underline-offset-4 hover:underline",
        // Fixed brand primary (not module-themed)
        "brand-primary": "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
        // Neutral outline (not module-themed)
        "outline-neutral": "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        // Neutral ghost (not module-themed)
        "ghost-neutral": "hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
